import { useTranslation } from 'react-i18next';
import { PaymentStatusBadge, FacturationStatusBadge, PaymentCommissionStatusBadge } from "./StatusBadges";
import PdfActions from "../components/PdfActions";
import { useFacturationActions } from "../../service/Hooks/useFacturationActions";
import { calculateTotalTTC, getFacturationState } from "../../service/utils/facturationUtils";
import { formatAmountInThousands, formatDate } from "../../service/utils/formatters";
import ConfirmationModal from "../Modal/ConfirmationModal";

/**
 * LIGNE DE FACTURATION - Gère l'affichage et les actions d'une facturation
 */
const FacturationRow = ({ 
  facturation, 
  onRefresh, 
  selectedDomain, 
  onDeleteClick, 
  onGeneratePdf, 
  generatingPdf 
}) => {
  const { t } = useTranslation('facturation');
  const { handleValidate, handlePayCommission, handleFinalPayment, isLoading, confirmationModal, hideConfirmation } = useFacturationActions(onRefresh);
  
  const state = getFacturationState(facturation);
  const totalTTC = calculateTotalTTC(facturation);

  return (
    <>
      <tr className="facturation-row">
        {/* Colonne Client */}
        <td>
          <div className="d-flex align-items-center">
            <div className="avatar-sm bg-primary bg-opacity-10 rounded-circle me-3">
              <span className="avatar-title text-black fw-bold">
                {facturation.accountName?.charAt(0)?.toUpperCase() || 'C'}
              </span>
            </div>
            <div>
              <h6 className="mb-0 fw-semibold">{facturation.accountName}</h6>
            </div>
          </div>
        </td>

        {/* Colonne Total AUC */}
        <td>
          <span className="fw-semibold text-dark">
            {formatAmountInThousands(facturation.totalAuc)}
          </span>
        </td>

        {/* Colonne Total Cash */}
        <td>
          <span className="fw-semibold text-dark">
            {formatAmountInThousands(facturation.accountCash)}
          </span>
        </td>

        {/* Colonne Commission */}
        <td>
          <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">
            {facturation.commissions}%
          </span>
        </td>

        {/* Colonne Statut de commission */}
        <td className="text-center">
          <PaymentCommissionStatusBadge facturation={facturation} />
        </td>

        {/* Colonne Statut Paiement */}
        <td className="text-center">
          <PaymentStatusBadge facturation={facturation} />
        </td>

        {/* Colonne Statut Facturation */}
        <td className="text-center">
          <FacturationStatusBadge facturation={facturation} />
        </td>

        {/* Colonne Net à Payer */}
        <td className="text-center">
          <span className="fw-bold text-success">
            {formatAmountInThousands(totalTTC)}
          </span>
        </td>

        {/* Colonne Date */}
        <td className="text-center">
          <small className="text-muted">
            {formatDate(facturation.facturationDate)}
          </small>
        </td>

        {/* Colonne PDF */}
        <td className="text-center">
          {facturation.image ? (
            <PdfActions 
              facturation={facturation}
              domainId={selectedDomain?.id}
            />
          ) : (
            <button
              className="btn btn-outline-warning btn-sm"
              onClick={() => onGeneratePdf(facturation.id)}
              disabled={generatingPdf === facturation.id}
              data-tooltip-id="tooltip-generate"
              data-tooltip-content={t('rowActions.generatePdf.tooltip')}
            >
              {generatingPdf === facturation.id ? (
                <span className="spinner-border spinner-border-sm" role="status"></span>
              ) : (
                <i className="fas fa-file-pdf"></i>
              )}
            </button>
          )}
        </td>

        {/* Colonne Actions */}
        <td className="text-center">
          <div className="btn-group btn-group-sm" role="group" style={{ zIndex:1 }}>
            {/* ÉTAPE 1: Validation */}
            {state.canValidate && (
              <ActionButton
                type="validate"
                loading={isLoading(facturation.id, 'validate')}
                onClick={() => handleValidate(facturation)}
                t={t}
              />
            )}

            {/* ÉTAPE 2: Paiement Commission */}
            {state.canPayCommission && (
              <ActionButton
                type="commission"
                loading={isLoading(facturation.id, 'commission')}
                onClick={() => handlePayCommission(facturation)}
                t={t}
              />
            )}

            {/* ÉTAPE 3: Paiement Final */}
            {state.canFinalPay && (
              <ActionButton
                type="final"
                loading={isLoading(facturation.id, 'final')}
                onClick={() => handleFinalPayment(facturation)}
                t={t}
              />
            )}

            {/* Vue seule si terminé */}
            {state.canViewOnly && (
              <button
                className="btn btn-outline-success"
                disabled
                data-tooltip-id="tooltip-completed"
                data-tooltip-content={t('rowActions.completed.tooltip')}
              >
                <i className="fas fa-flag-checkered"></i>
              </button>
            )}

            {/* Vue détails */}
            <button
              className="btn btn-outline-info"
              data-bs-toggle="modal"
              data-bs-target="#detailFactureModal"
              data-facturation-id={facturation.id}
              data-tooltip-id="tooltip-view"
              data-tooltip-content={t('rowActions.view.tooltip')}
            >
              <i className="fas fa-eye"></i>
            </button>

            {/* Suppression */}
            {state.canDelete && (
              <button
                className="btn btn-outline-danger"
                onClick={() => onDeleteClick(facturation)}
                data-tooltip-id="tooltip-delete"
                data-tooltip-content={t('rowActions.delete.tooltip')}
              >
                <i className="fas fa-trash"></i>
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Modal de Confirmation */}
      <ConfirmationModal
        show={confirmationModal?.show && confirmationModal?.facturation?.id === facturation.id}
        type={confirmationModal.type}
        facturation={confirmationModal.facturation}
        message={confirmationModal.message}
        onConfirm={confirmationModal.onConfirm}
        onCancel={hideConfirmation}
        loading={isLoading(facturation.id, confirmationModal.type)}
        t={t}
      />
    </>
  );
};

/**
 * BOUTON D'ACTION RÉUTILISABLE
 */
const ActionButton = ({ type, loading, onClick, t }) => {
  const buttonConfig = {
    validate: {
      icon: 'fas fa-check-circle',
      tooltip: t('rowActions.validate.tooltip'),
      className: 'btn-outline-primary',
      content: t('rowActions.validate.label')
    },
    commission: {
      icon: 'fas fa-credit-card',
      tooltip: t('rowActions.commission.tooltip'),
      className: 'btn-outline-warning',
      content: t('rowActions.commission.label')
    },
    final: {
      icon: 'fas fa-money-bill-wave',
      tooltip: t('rowActions.final.tooltip'),
      className: 'btn-outline-success',
      content: t('rowActions.final.label')
    }
  };

  const config = buttonConfig[type];

  return (
    <button
      className={`btn ${config.className}`}
      onClick={onClick}
      disabled={loading}
      data-tooltip-id={`tooltip-${type}`}
      data-tooltip-content={config.tooltip}
    >
      {loading ? (
        <span className="spinner-border spinner-border-sm" role="status"></span>
      ) : (
        <>
          <i className={`${config.icon} me-1`}></i>
          {config.content}
        </>
      )}
    </button>
  );
};

export default FacturationRow;